import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, CheckCircle, XCircle, RotateCcw } from 'lucide-react';

// ─── Voucher type metadata ────────────────────────────────────────────────────
const VOUCHER_TYPES = {
  CRV: { label: 'Cash Receipt Voucher',  short: 'CRV', color: '#166534', bg: '#dcfce7', desc: 'Cash received from customers or other sources. Debit: Cash / Credit: Revenue or AR' },
  CPV: { label: 'Cash Payment Voucher',  short: 'CPV', color: '#7f1d1d', bg: '#fee2e2', desc: 'Cash paid for expenses or vendors. Debit: Expense / Credit: Cash' },
  BRV: { label: 'Bank Receipt Voucher',  short: 'BRV', color: '#1e40af', bg: '#dbeafe', desc: 'Funds received in bank (transfer/cheque). Debit: Bank / Credit: AR or Revenue' },
  BPV: { label: 'Bank Payment Voucher',  short: 'BPV', color: '#7c3aed', bg: '#ede9fe', desc: 'Payments made via bank. Debit: Expense/AP / Credit: Bank' },
  JV:  { label: 'Journal Voucher',       short: 'JV',  color: '#92400e', bg: '#fef3c7', desc: 'Adjustment or correction entry. Both debit and credit must balance.' },
};

const STATUS_BADGE = {
  DRAFT:             'badge-gray',
  PENDING_APPROVAL:  'badge-warning',
  APPROVED:          'badge-info',
  POSTED:            'badge-success',
  REVERSED:          'badge-danger',
};

// ─── VOUCHER ENTRY FORM ───────────────────────────────────────────────────────
function VoucherForm({ onClose, onSaved, accounts }) {
  const [form, setForm] = useState({
    voucherType: 'CRV', amount: '', description: '', referenceNo: '',
    bankAccount: '', chequeNo: '', chequeDate: '', partyType: 'customer',
    partyName: '', voucherDate: new Date().toISOString().slice(0,10),
  });
  const [lines, setLines] = useState([
    { accountCode: '', debit: '', credit: '', description: '' },
    { accountCode: '', debit: '', credit: '', description: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const vt = VOUCHER_TYPES[form.voucherType];
  const totalDr = lines.reduce((s, l) => s + parseFloat(l.debit || 0), 0);
  const totalCr = lines.reduce((s, l) => s + parseFloat(l.credit || 0), 0);
  const balanced = Math.abs(totalDr - totalCr) < 0.01;

  const updateLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLine    = () => setLines(ls => [...ls, { accountCode: '', debit: '', credit: '', description: '' }]);
  const removeLine = (i) => { if (lines.length <= 2) return; setLines(ls => ls.filter((_, idx) => idx !== i)); };

  // Auto-suggest default lines based on voucher type
  const applyTemplate = (type) => {
    const templates = {
      CRV: [{ accountCode: '1010', debit: '', credit: '' }, { accountCode: '4010', debit: '', credit: '' }],
      CPV: [{ accountCode: '5000', debit: '', credit: '' }, { accountCode: '1010', debit: '', credit: '' }],
      BRV: [{ accountCode: '1020', debit: '', credit: '' }, { accountCode: '1040', debit: '', credit: '' }],
      BPV: [{ accountCode: '5000', debit: '', credit: '' }, { accountCode: '1020', debit: '', credit: '' }],
      JV:  [{ accountCode: '',     debit: '', credit: '' }, { accountCode: '',     debit: '', credit: '' }],
    };
    setLines((templates[type] || templates.JV).map(t => ({ ...t, description: '' })));
  };

  const handleTypeChange = (type) => {
    setForm(f => ({ ...f, voucherType: type }));
    applyTemplate(type);
  };

  const submit = async () => {
    if (!balanced) { toast.error('Debit must equal Credit'); return; }
    if (!form.amount) { toast.error('Amount required'); return; }
    const filledLines = lines.filter(l => l.accountCode && (parseFloat(l.debit||0) > 0 || parseFloat(l.credit||0) > 0));
    if (filledLines.length < 2) { toast.error('At least 2 GL lines required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/finance/vouchers', { ...form, lines: filledLines }).then(r => r.data);
      if (r.success) { toast.success(`${vt.short} created: ${r.data.voucher_no}`); onSaved(r.data); }
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to create voucher'); }
    finally { setSaving(false); }
  };

  const isBankVoucher = ['BRV', 'BPV'].includes(form.voucherType);

  return (
    <Modal open onClose={onClose} title={`New ${vt.label}`} size="modal-lg">
      <div className="modal-body">
        {/* Voucher type selector */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 6, marginBottom: 16 }}>
          {Object.entries(VOUCHER_TYPES).map(([k, v]) => (
            <button key={k} onClick={() => handleTypeChange(k)}
              style={{ padding: '8px 4px', border: `2px solid ${form.voucherType === k ? v.color : 'var(--gray-200)'}`,
                borderRadius: 8, background: form.voucherType === k ? v.bg : 'white',
                cursor: 'pointer', fontSize: '.72rem', fontWeight: 600, color: v.color, textAlign: 'center' }}>
              {k}
              <div style={{ fontSize: '.62rem', color: 'var(--gray-500)', fontWeight: 400, marginTop: 2 }}>
                {v.label.split(' ').slice(0, 2).join(' ')}
              </div>
            </button>
          ))}
        </div>

        <div className="alert alert-info" style={{ fontSize: '.8rem', padding: '8px 12px', marginBottom: 12 }}>
          {vt.desc}
        </div>

        {/* Header fields */}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Amount (PKR) <span className="req">*</span></label>
            <input className="form-control" type="number" step="0.01" min="0"
              value={form.amount} onChange={set('amount')} />
          </div>
          <div className="form-group">
            <label className="form-label">Voucher Date</label>
            <input className="form-control" type="date" value={form.voucherDate} onChange={set('voucherDate')} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Party Type</label>
            <select className="form-control" value={form.partyType} onChange={set('partyType')}>
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
              <option value="employee">Employee</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Party Name</label>
            <input className="form-control" placeholder="Customer / vendor name" value={form.partyName} onChange={set('partyName')} />
          </div>
        </div>

        {isBankVoucher && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Bank Account</label>
              <select className="form-control" value={form.bankAccount} onChange={set('bankAccount')}>
                <option value="">— Select bank —</option>
                <option value="HBL-001">HBL - Current A/C (...1234)</option>
                <option value="MCB-001">MCB - Current A/C (...5678)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Cheque No</label>
              <input className="form-control" placeholder="Cheque number" value={form.chequeNo} onChange={set('chequeNo')} />
            </div>
            <div className="form-group">
              <label className="form-label">Cheque Date</label>
              <input className="form-control" type="date" value={form.chequeDate} onChange={set('chequeDate')} />
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Description / Narration</label>
          <input className="form-control" placeholder="e.g. Cash received from Ali Raza for invoice INV-202604-001"
            value={form.description} onChange={set('description')} />
        </div>

        {!isBankVoucher && (
          <div className="form-group">
            <label className="form-label">Reference No</label>
            <input className="form-control" placeholder="Receipt / transaction reference" value={form.referenceNo} onChange={set('referenceNo')} />
          </div>
        )}

        {/* GL Lines */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 600, fontSize: '.85rem' }}>GL Lines (Double-Entry)</div>
            <button className="btn btn-outline btn-sm" onClick={addLine}><Plus size={12} /> Add Line</button>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid var(--gray-200)' }}>Account</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', border: '1px solid var(--gray-200)', width: 110 }}>Debit (PKR)</th>
                <th style={{ padding: '6px 8px', textAlign: 'right', border: '1px solid var(--gray-200)', width: 110 }}>Credit (PKR)</th>
                <th style={{ padding: '6px 8px', textAlign: 'left', border: '1px solid var(--gray-200)' }}>Description</th>
                <th style={{ width: 28, border: '1px solid var(--gray-200)' }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td style={{ padding: 4, border: '1px solid var(--gray-200)' }}>
                    <select style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '.78rem', padding: 2 }}
                      value={line.accountCode} onChange={e => updateLine(i, 'accountCode', e.target.value)}>
                      <option value="">— Select account —</option>
                      {Object.entries(
                        (accounts || []).reduce((g, a) => { (g[a.type] = g[a.type] || []).push(a); return g; }, {})
                      ).map(([type, accs]) => (
                        <optgroup key={type} label={type}>
                          {accs.map(a => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                        </optgroup>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: 4, border: '1px solid var(--gray-200)' }}>
                    <input type="number" step="0.01" min="0" placeholder="0.00"
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right', fontSize: '.82rem', color: 'var(--danger)', padding: 2 }}
                      value={line.debit} onChange={e => updateLine(i, 'debit', e.target.value)} />
                  </td>
                  <td style={{ padding: 4, border: '1px solid var(--gray-200)' }}>
                    <input type="number" step="0.01" min="0" placeholder="0.00"
                      style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'right', fontSize: '.82rem', color: 'var(--success)', padding: 2 }}
                      value={line.credit} onChange={e => updateLine(i, 'credit', e.target.value)} />
                  </td>
                  <td style={{ padding: 4, border: '1px solid var(--gray-200)' }}>
                    <input placeholder="Description" style={{ width: '100%', border: 'none', background: 'transparent', fontSize: '.78rem', padding: 2 }}
                      value={line.description} onChange={e => updateLine(i, 'description', e.target.value)} />
                  </td>
                  <td style={{ padding: 4, border: '1px solid var(--gray-200)', textAlign: 'center' }}>
                    <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '.8rem' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: balanced ? '#f0fdf4' : '#fef2f2' }}>
                <td style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', textAlign: 'right', fontSize: '.8rem' }}>Totals</td>
                <td style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', textAlign: 'right', color: 'var(--danger)' }}>{fmtPKR(totalDr)}</td>
                <td style={{ padding: '6px 8px', border: '1px solid var(--gray-200)', textAlign: 'right', color: 'var(--success)' }}>{fmtPKR(totalCr)}</td>
                <td colSpan={2} style={{ padding: '6px 8px', border: '1px solid var(--gray-200)' }}>
                  {balanced
                    ? <span style={{ color: 'var(--success)', fontSize: '.78rem' }}>✓ Balanced</span>
                    : <span style={{ color: 'var(--danger)', fontSize: '.78rem' }}>✗ Difference: {fmtPKR(Math.abs(totalDr - totalCr))}</span>
                  }
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || !balanced} aria-busy={saving}>
          {saving ? 'Saving…' : 'Create Voucher'}
        </button>
      </div>
    </Modal>
  );
}

// ─── VOUCHER LIST PAGE ────────────────────────────────────────────────────────
export function VouchersPage() {
  const [vouchers, setVouchers]   = useState([]);
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [filters, setFilters]     = useState({ voucherType: '', status: '', fromDate: '', toDate: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v)));
      const [vr, ar] = await Promise.all([
        api.get(`/finance/vouchers?${params}`).then(r => r.data),
        api.get('/finance/coa').then(r => r.data),
      ]);
      if (vr.success) setVouchers(vr.data);
      if (ar.success) setAccounts(ar.data);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const action = async (voucherId, act, body = {}) => {
    try {
      const endpoint = act === 'reverse'
        ? `/finance/vouchers/${voucherId}/reverse`
        : `/finance/vouchers/${voucherId}/${act}`;
      const method = act === 'reverse' ? 'post' : 'patch';
      await api[method](endpoint, body).then(r => r.data);
      toast.success(`Voucher ${act}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Action failed'); }
  };

  const totals = vouchers.reduce((s, v) => {
    if (v.status === 'POSTED') {
      if (['CRV','BRV'].includes(v.voucher_type)) s.receipts += parseFloat(v.amount||0);
      else if (['CPV','BPV'].includes(v.voucher_type)) s.payments += parseFloat(v.amount||0);
    }
    return s;
  }, { receipts: 0, payments: 0 });

  return (
    <AppShell title="Vouchers — Cash & Bank Management" actions={
      <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14}/> New Voucher</button>
    }>
      {/* Summary */}
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
        {Object.entries(VOUCHER_TYPES).map(([k, v]) => {
          const count = vouchers.filter(x => x.voucher_type === k).length;
          return (
            <div key={k} className="stat-card" style={{ borderTop: `3px solid ${v.color}` }}>
              <div className="stat-label">{v.short}</div>
              <div style={{ fontWeight: 700, color: v.color, fontSize: '1.1rem', marginTop: 4 }}>{count}</div>
              <div style={{ fontSize: '.65rem', color: 'var(--gray-400)', marginTop: 2 }}>{v.label.replace(' Voucher','')}</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <select className="form-control" style={{ width: 160 }} value={filters.voucherType}
          onChange={e => setFilters(f => ({ ...f, voucherType: e.target.value }))}>
          <option value="">All Types</option>
          {Object.entries(VOUCHER_TYPES).map(([k, v]) => <option key={k} value={k}>{k} — {v.label}</option>)}
        </select>
        <select className="form-control" style={{ width: 160 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">All Statuses</option>
          {['DRAFT','PENDING_APPROVAL','APPROVED','POSTED','REVERSED'].map(s =>
            <option key={s} value={s}>{s.replace('_',' ')}</option>
          )}
        </select>
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.fromDate}
          onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} />
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.toDate}
          onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} />
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : vouchers.length ? (
            <table>
              <thead>
                <tr><th>Voucher No</th><th>Type</th><th>Date</th><th>Party</th><th>Description</th><th style={{ textAlign:'right' }}>Amount</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {vouchers.map(v => {
                  const vt = VOUCHER_TYPES[v.voucher_type];
                  return (
                    <tr key={v.voucher_id}>
                      <td><code style={{ fontSize: '.78rem', fontWeight: 600 }}>{v.voucher_no}</code></td>
                      <td>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: '.68rem', fontWeight: 600, background: vt.bg, color: vt.color }}>
                          {v.voucher_type}
                        </span>
                      </td>
                      <td style={{ fontSize: '.8rem', fontFamily: 'monospace' }}>{fmtDate(v.voucher_date)}</td>
                      <td>
                        <div style={{ fontSize: '.82rem', fontWeight: 500 }}>{v.party_name || '—'}</div>
                        <div style={{ fontSize: '.7rem', color: 'var(--gray-400)' }}>{v.party_type}</div>
                      </td>
                      <td style={{ maxWidth: 200 }}>
                        <span style={{ fontSize: '.8rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {v.description || '—'}
                        </span>
                        {v.cheque_no && <span style={{ fontSize: '.68rem', color: 'var(--gray-400)' }}>Chq: {v.cheque_no}</span>}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: ['CRV','BRV'].includes(v.voucher_type) ? 'var(--success)' : 'var(--danger)' }}>
                        {fmtPKR(v.amount)}
                      </td>
                      <td><span className={`badge ${STATUS_BADGE[v.status]||'badge-gray'}`} style={{ fontSize: '.65rem' }}>{v.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {v.status === 'DRAFT' && (
                            <>
                              <button className="btn btn-success btn-sm" style={{ fontSize: '.68rem' }} onClick={() => action(v.voucher_id, 'approve')}>
                                <CheckCircle size={11}/> Approve
                              </button>
                              <button className="btn btn-primary btn-sm" style={{ fontSize: '.68rem' }} onClick={() => action(v.voucher_id, 'post')}>
                                Post
                              </button>
                            </>
                          )}
                          {v.status === 'APPROVED' && (
                            <button className="btn btn-primary btn-sm" style={{ fontSize: '.68rem' }} onClick={() => action(v.voucher_id, 'post')}>
                              Post to GL
                            </button>
                          )}
                          {v.status === 'POSTED' && (
                            <button className="btn btn-danger btn-sm" style={{ fontSize: '.68rem' }}
                              onClick={() => { const r = window.prompt('Reason for reversal:'); if (r) action(v.voucher_id, 'reverse', { reason: r }); }}>
                              <RotateCcw size={11}/> Reverse
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <EmptyState icon="📋" title="No vouchers yet" message={<button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>Create First Voucher</button>} />}
        </div>
      </div>

      {showAdd && <VoucherForm accounts={accounts} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </AppShell>
  );
}

// ─── CHART OF ACCOUNTS PAGE ───────────────────────────────────────────────────
export function ChartOfAccountsPage() {
  const [accounts, setAccounts]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [editCode, setEditCode]   = useState(null);
  const [filterType, setFilterType] = useState('');
  const [form, setForm] = useState({ code: '', name: '', type: 'ASSET', subType: '', parentCode: '', description: '' });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/coa${filterType ? `?type=${filterType}` : ''}`).then(r => r.data);
      if (r.success) setAccounts(r.data);
    } finally { setLoading(false); }
  }, [filterType]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.code || !form.name || !form.type) { toast.error('Code, name and type required'); return; }
    try {
      if (editCode) {
        await api.patch('/finance/coa/'+editCode, { name:form.name, subType:form.subType, description:form.description });
        toast.success('Account updated');
      } else {
        const r = await api.post('/finance/coa', form).then(r => r.data);
        if (!r.success) throw new Error(r.message);
        toast.success('Account created');
      }
      setShowAdd(false); setEditCode(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const TYPE_COLORS = { ASSET: '#1e40af', LIABILITY: '#dc2626', EQUITY: '#7c3aed', REVENUE: '#166534', EXPENSE: '#92400e' };
  const grouped = accounts.reduce((g, a) => { (g[a.type] = g[a.type] || []).push(a); return g; }, {});

  return (
    <AppShell title="Chart of Accounts" actions={
      <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14}/> Add Account</button>
    }>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['', 'ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'].map(t => (
          <button key={t} onClick={() => setFilterType(t)}
            className={`btn btn-sm ${filterType === t ? 'btn-primary' : 'btn-outline'}`}>
            {t || 'All'}
          </button>
        ))}
      </div>

      {loading ? <Spinner /> : (
        <div style={{ display: 'grid', gap: 16 }}>
          {Object.entries(grouped).map(([type, accs]) => (
            <div key={type} className="card">
              <div className="card-header" style={{ background: `${TYPE_COLORS[type] || '#666'}15`, borderBottom: `2px solid ${TYPE_COLORS[type] || '#666'}` }}>
                <span className="card-title" style={{ color: TYPE_COLORS[type] }}>{type}</span>
                <span style={{ fontSize: '.75rem', color: 'var(--gray-400)' }}>{accs.length} accounts</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Code</th><th>Account Name</th><th>Sub-Type</th><th>Parent</th><th>System</th></tr></thead>
                  <tbody>
                    {accs.map(a => (
                      <tr key={a.account_id}>
                        <td><code style={{ fontSize: '.82rem', fontWeight: 700, color: TYPE_COLORS[type] }}>{a.code}</code></td>
                        <td style={{ fontWeight: a.parent_code ? 400 : 500, paddingLeft: a.parent_code ? 24 : 8 }}>
                          {a.parent_code && <span style={{ color: 'var(--gray-300)', marginRight: 6 }}>└</span>}
                          {a.name}
                        </td>
                        <td><span className="badge badge-gray" style={{ fontSize: '.65rem' }}>{a.sub_type || '—'}</span></td>
                        <td style={{ fontSize: '.78rem', color: 'var(--gray-400)' }}>{a.parent_code || '—'}</td>
                        <td>{a.is_system && <span style={{ color: 'var(--amber)', fontSize: '.72rem' }}>🔒 System</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={showAdd} onClose={() => { setShowAdd(false); setEditCode(null); }} title={editCode ? 'Edit Account' : 'New Account'}>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Code <span className="req">*</span></label>
              <input className="form-control" placeholder="e.g. 1025" value={form.code} onChange={set('code')} />
            </div>
            <div className="form-group">
              <label className="form-label">Type <span className="req">*</span></label>
              <select className="form-control" value={form.type} onChange={set('type')}>
                {['ASSET','LIABILITY','EQUITY','REVENUE','EXPENSE'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Account Name <span className="req">*</span></label>
            <input className="form-control" placeholder="e.g. Petty Cash" value={form.name} onChange={set('name')} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Sub-Type</label>
              <input className="form-control" placeholder="e.g. CASH, BANK, PAYROLL" value={form.subType} onChange={set('subType')} />
            </div>
            <div className="form-group">
              <label className="form-label">Parent Account</label>
              <select className="form-control" value={form.parentCode} onChange={set('parentCode')}>
                <option value="">— None (top-level) —</option>
                {accounts.filter(a => a.type === form.type).map(a => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2} value={form.description} onChange={set('description')} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>{editCode ? 'Save Changes' : 'Save Account'}</button>
        </div>
      </Modal>
    </AppShell>
  );
}

// ─── TRIAL BALANCE PAGE ───────────────────────────────────────────────────────
export function TrialBalancePage() {
  const [data, setData]     = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0,10),
    toDate:   new Date().toISOString().slice(0,10),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/trial-balance?fromDate=${filters.fromDate}&toDate=${filters.toDate}`).then(r => r.data);
      if (r.success) { setData(r.data); setSummary(r.summary); }
    } finally { setLoading(false); }
  }, [filters]);

  const TYPE_COLORS = { ASSET: '#1e40af', LIABILITY: '#dc2626', EQUITY: '#7c3aed', REVENUE: '#166534', EXPENSE: '#92400e' };

  return (
    <AppShell title="Trial Balance" actions={
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.fromDate}
          onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} />
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.toDate}
          onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} />
        <button className="btn btn-primary btn-sm" onClick={load} disabled={loading}>{loading ? '…' : 'Generate'}</button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}>🖨️ Print</button>
      </div>
    }>
      {summary.isBalanced !== undefined && (
        <div className={`alert ${summary.isBalanced ? 'alert-success' : 'alert-danger'}`} style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          {summary.isBalanced ? <CheckCircle size={16}/> : <XCircle size={16}/>}
          <span style={{ fontWeight: 600 }}>
            {summary.isBalanced ? 'Books are balanced — Debit = Credit' : `Out of balance by ${fmtPKR(Math.abs((summary.totalDebit||0) - (summary.totalCredit||0)))}`}
          </span>
        </div>
      )}

      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Total Debits</div><div className="stat-value" style={{ color: 'var(--danger)', fontSize: '1.1rem' }}>{fmtPKR(summary.totalDebit)}</div></div>
        <div className="stat-card"><div className="stat-label">Total Credits</div><div className="stat-value" style={{ color: 'var(--success)', fontSize: '1.1rem' }}>{fmtPKR(summary.totalCredit)}</div></div>
        <div className="stat-card">
          <div className="stat-label">Balance</div>
          <div className="stat-value" style={{ color: summary.isBalanced ? 'var(--success)' : 'var(--danger)', fontSize: '1.1rem' }}>
            {summary.isBalanced ? '✓ Balanced' : fmtPKR(Math.abs((summary.totalDebit||0)-(summary.totalCredit||0)))}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : data.length ? (
            <table>
              <thead>
                <tr>
                  <th>Code</th><th>Account Name</th><th>Type</th>
                  <th style={{ textAlign: 'right' }}>Debit (PKR)</th>
                  <th style={{ textAlign: 'right' }}>Credit (PKR)</th>
                  <th style={{ textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}>
                    <td><code style={{ fontSize: '.8rem' }}>{row.code}</code></td>
                    <td style={{ fontWeight: 500 }}>{row.name}</td>
                    <td><span style={{ fontSize: '.65rem', fontWeight: 600, color: TYPE_COLORS[row.type] || '#666' }}>{row.type}</span></td>
                    <td style={{ textAlign: 'right', color: 'var(--danger)', fontFamily: 'monospace', fontSize: '.82rem' }}>
                      {parseFloat(row.total_debit||0) > 0 ? fmtPKR(row.total_debit) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontFamily: 'monospace', fontSize: '.82rem' }}>
                      {parseFloat(row.total_credit||0) > 0 ? fmtPKR(row.total_credit) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontFamily: 'monospace', fontSize: '.82rem',
                      color: parseFloat(row.balance||0) >= 0 ? 'var(--navy)' : 'var(--danger)' }}>
                      {fmtPKR(Math.abs(row.balance||0))}{parseFloat(row.balance||0) < 0 ? ' Cr' : ' Dr'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700, background: 'var(--gray-50)' }}>
                  <td colSpan={3} style={{ textAlign: 'right' }}>Grand Total</td>
                  <td style={{ textAlign: 'right', color: 'var(--danger)' }}>{fmtPKR(summary.totalDebit)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--success)' }}>{fmtPKR(summary.totalCredit)}</td>
                  <td style={{ textAlign: 'right', color: summary.isBalanced ? 'var(--success)' : 'var(--danger)' }}>
                    {summary.isBalanced ? '✓' : '✗'}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : <EmptyState icon="⚖️" title="Select date range and click Generate" />}
        </div>
      </div>
    </AppShell>
  );
}

// ─── CASH BOOK PAGE ───────────────────────────────────────────────────────────
export function CashBookPage() {
  const [data, setData]     = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ fromDate: new Date().toISOString().slice(0,10), toDate: new Date().toISOString().slice(0,10) });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/cash-book?fromDate=${filters.fromDate}&toDate=${filters.toDate}`).then(r => r.data);
      if (r.success) { setData(r.data); setSummary(r.summary); }
    } finally { setLoading(false); }
  }, [filters]);

  return (
    <AppShell title="Cash Book" actions={
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.fromDate} onChange={e => setFilters(f => ({...f, fromDate: e.target.value}))} />
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.toDate}   onChange={e => setFilters(f => ({...f, toDate: e.target.value}))} />
        <button className="btn btn-primary btn-sm" onClick={load} disabled={loading}>{loading ? '…' : 'Load'}</button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}>🖨️ Print</button>
      </div>
    }>
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Cash Receipts (CRV)</div><div className="stat-value" style={{ color: 'var(--success)', fontSize: '1.1rem' }}>{fmtPKR(summary.totalReceipts)}</div></div>
        <div className="stat-card"><div className="stat-label">Cash Payments (CPV)</div><div className="stat-value" style={{ color: 'var(--danger)', fontSize: '1.1rem' }}>{fmtPKR(summary.totalPayments)}</div></div>
        <div className="stat-card"><div className="stat-label">Closing Cash Balance</div><div className="stat-value" style={{ color: 'var(--navy)', fontSize: '1.1rem' }}>{fmtPKR(summary.closingBalance)}</div></div>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : data.length ? (
            <table>
              <thead><tr><th>Date</th><th>Voucher No</th><th>Narration</th><th>Party</th><th style={{ textAlign:'right' }}>Receipts (Dr)</th><th style={{ textAlign:'right' }}>Payments (Cr)</th></tr></thead>
              <tbody>
                {data.map(v => (
                  <tr key={v.voucher_id}>
                    <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{fmtDate(v.voucher_date)}</td>
                    <td><code style={{ fontSize:'.78rem', color: v.voucher_type==='CRV'?'var(--success)':'var(--danger)' }}>{v.voucher_no}</code></td>
                    <td style={{ fontSize:'.82rem' }}>{v.description||'—'}</td>
                    <td style={{ fontSize:'.78rem' }}>{v.party_name||'—'}</td>
                    <td style={{ textAlign:'right', fontWeight:600, color:'var(--success)' }}>{v.voucher_type==='CRV' ? fmtPKR(v.amount) : '—'}</td>
                    <td style={{ textAlign:'right', fontWeight:600, color:'var(--danger)' }}>{v.voucher_type==='CPV' ? fmtPKR(v.amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight:700, background:'var(--gray-50)' }}>
                  <td colSpan={4} style={{ textAlign:'right' }}>Totals</td>
                  <td style={{ textAlign:'right', color:'var(--success)' }}>{fmtPKR(summary.totalReceipts)}</td>
                  <td style={{ textAlign:'right', color:'var(--danger)' }}>{fmtPKR(summary.totalPayments)}</td>
                </tr>
              </tfoot>
            </table>
          ) : <EmptyState icon="💰" title="No cash transactions" message="Load a date range to view the cash book." />}
        </div>
      </div>
    </AppShell>
  );
}

// ─── BANK BOOK PAGE ───────────────────────────────────────────────────────────
export function BankBookPage() {
  const [data, setData]     = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ fromDate: new Date().toISOString().slice(0,10), toDate: new Date().toISOString().slice(0,10), bankAccount: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([,v]) => v)));
      const r = await api.get(`/finance/bank-book?${p}`).then(r => r.data);
      if (r.success) { setData(r.data); setSummary(r.summary); }
    } finally { setLoading(false); }
  }, [filters]);

  return (
    <AppShell title="Bank Book" actions={
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="form-control" style={{ width: 160 }} value={filters.bankAccount} onChange={e => setFilters(f => ({...f, bankAccount: e.target.value}))}>
          <option value="">All Banks</option>
          <option value="HBL-001">HBL - Current A/C</option>
          <option value="MCB-001">MCB - Current A/C</option>
        </select>
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.fromDate} onChange={e => setFilters(f => ({...f, fromDate: e.target.value}))} />
        <input className="form-control" type="date" style={{ width: 140 }} value={filters.toDate}   onChange={e => setFilters(f => ({...f, toDate: e.target.value}))} />
        <button className="btn btn-primary btn-sm" onClick={load} disabled={loading}>{loading ? '…' : 'Load'}</button>
        <button className="btn btn-outline btn-sm" onClick={() => window.print()}>🖨️ Print</button>
      </div>
    }>
      <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        <div className="stat-card"><div className="stat-label">Bank Receipts (BRV)</div><div className="stat-value" style={{ color:'var(--success)', fontSize:'1.1rem' }}>{fmtPKR(summary.totalReceipts)}</div></div>
        <div className="stat-card"><div className="stat-label">Bank Payments (BPV)</div><div className="stat-value" style={{ color:'var(--danger)', fontSize:'1.1rem' }}>{fmtPKR(summary.totalPayments)}</div></div>
        <div className="stat-card"><div className="stat-label">Bank Balance</div><div className="stat-value" style={{ color:'var(--navy)', fontSize:'1.1rem' }}>{fmtPKR(summary.closingBalance)}</div></div>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : data.length ? (
            <table>
              <thead><tr><th>Date</th><th>Voucher No</th><th>Narration</th><th>Party</th><th>Cheque No</th><th style={{ textAlign:'right' }}>Deposits (Dr)</th><th style={{ textAlign:'right' }}>Withdrawals (Cr)</th></tr></thead>
              <tbody>
                {data.map(v => (
                  <tr key={v.voucher_id}>
                    <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{fmtDate(v.voucher_date)}</td>
                    <td><code style={{ fontSize:'.78rem', color: v.voucher_type==='BRV'?'var(--success)':'var(--danger)' }}>{v.voucher_no}</code></td>
                    <td style={{ fontSize:'.82rem' }}>{v.description||'—'}</td>
                    <td style={{ fontSize:'.78rem' }}>{v.party_name||'—'}</td>
                    <td style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>{v.cheque_no||'—'}</td>
                    <td style={{ textAlign:'right', fontWeight:600, color:'var(--success)' }}>{v.voucher_type==='BRV' ? fmtPKR(v.amount) : '—'}</td>
                    <td style={{ textAlign:'right', fontWeight:600, color:'var(--danger)' }}>{v.voucher_type==='BPV' ? fmtPKR(v.amount) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight:700, background:'var(--gray-50)' }}>
                  <td colSpan={5} style={{ textAlign:'right' }}>Totals</td>
                  <td style={{ textAlign:'right', color:'var(--success)' }}>{fmtPKR(summary.totalReceipts)}</td>
                  <td style={{ textAlign:'right', color:'var(--danger)' }}>{fmtPKR(summary.totalPayments)}</td>
                </tr>
              </tfoot>
            </table>
          ) : <EmptyState icon="🏦" title="No bank transactions" message="Load a date range to view the bank book." />}
        </div>
      </div>
    </AppShell>
  );
}
